import type { StateCreator } from 'zustand';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { DocumentsOnTemplateView } from 'presentation/document/documentsOnTemplateView';
import { showChannelRequestErrorToast } from 'services/toaster';
import { checklistAnswerClient } from 'shared/domain/checklistAnswer/checklistAnswerClient';
import { checklistSectionClient } from 'shared/domain/checklistSection/checklistSectionClient';
import { inspectionTemplateClient } from 'shared/domain/inspectionTemplate/inspectionTemplateClient';
import { subscribeToEntityChanges } from 'store/entityCollection';
import type { InspectionStoreState } from '../../store';
import type { InspectionTemplateSlice } from './types';

const EMPTY_DOCUMENTS: DocumentsOnTemplateView = {
  templateDocuments: [],
  checklistDocuments: {},
  sectionDocuments: {},
};

const getDefaultState = () => ({
  inspectionTemplate: undefined,
  inspectionTemplateDocuments: EMPTY_DOCUMENTS,
  templateId: undefined,
  loadingTemplate: false,
  loadingTemplateDocuments: true,
});

type InspectionTemplateSliceCreator = StateCreator<
  InspectionStoreState,
  [],
  [],
  InspectionTemplateSlice
>;

/**
 * The open template and the changes of templates, checklist items, sections
 * and answers, all through their domain clients. Reads are numbered: only the
 * latest one (of the template open now) lands in the store.
 */
export const createInspectionTemplateSlice: InspectionTemplateSliceCreator = (
  set,
  get
) => {
  /** Not state: bumped by every read and reset, so older answers are dropped. */
  let latestRead = 0;

  return {
    ...getDefaultState(),

    openInspectionTemplate: (id) => {
      const { templateId, inspectionTemplate, loadingTemplate } = get();
      if (id === templateId) {
        // Read again only a template that failed to load (a retry), never one shown or loading.
        if (!inspectionTemplate && !loadingTemplate) {
          void get().reloadInspectionTemplate();
        }
        return;
      }
      const isOtherTemplateShown = Boolean(id) && inspectionTemplate && !areObjectIdsEqual(inspectionTemplate._id, id);
      set({ templateId: id, ...(isOtherTemplateShown ? { inspectionTemplate: undefined } : {}) });
      void get().reloadInspectionTemplate();
    },

    reloadInspectionTemplate: async () => {
      const id = get().templateId;
      if (!id) {
        return;
      }
      const read = ++latestRead;
      const isLatest = (): boolean => read === latestRead;
      // A read of the template shown (a change, the return to the tab) refreshes it in the background:
      // flagged as loading, the inspection view would swap its form for a skeleton and lose its scroll (PT-7282).
      if (!areObjectIdsEqual(get().inspectionTemplate?._id, id)) {
        set({ loadingTemplate: true });
      }
      try {
        const inspectionTemplate = await inspectionTemplateClient.getOneById(id);
        if (isLatest()) {
          set({ inspectionTemplate });
        }
      } catch (error) {
        if (isLatest()) {
          showChannelRequestErrorToast('Error while getting inspection template', error);
        }
      } finally {
        if (isLatest()) {
          set({ loadingTemplate: false });
        }
      }
    },

    subscribeToInspectionTemplateChanges: () =>
      subscribeToEntityChanges(inspectionTemplateClient, () => void get().reloadInspectionTemplate()),

    setInspectionTemplateDocuments: (inspectionTemplateDocuments) =>
      set({ inspectionTemplateDocuments }),
    setLoadingTemplateDocuments: (loadingTemplateDocuments) =>
      set({ loadingTemplateDocuments }),

    createInspectionTemplate: (inspectionTemplateOutDto, projectId) =>
      inspectionTemplateClient.create({ inspectionTemplateOutDto, projectId }),
    updateInspectionTemplate: (templateId, inspectionTemplateOutDto, projectId) =>
      inspectionTemplateClient.edit({ templateId, inspectionTemplateOutDto, projectId }),
    removeInspectionTemplate: (templateId, projectId) =>
      inspectionTemplateClient.remove({ templateId, projectId }),
    publishInspectionTemplate: (templateId, projectId) =>
      inspectionTemplateClient.publish({ templateId, projectId }),

    createInspectionTemplateChecklistItems: (templateId, checklistItemsOutDto, projectId) =>
      inspectionTemplateClient.createChecklistItems({ templateId, checklistItemsOutDto, projectId }),
    updateInspectionTemplateChecklistItem: (templateId, checklistItemId, checklistItemOutDto, projectId) =>
      inspectionTemplateClient.editChecklistItem({ templateId, checklistItemId, checklistItemOutDto, projectId }),
    removeInspectionTemplateChecklistItem: (templateId, checklistItemId, projectId) =>
      inspectionTemplateClient.removeChecklistItem({ templateId, checklistItemId, projectId }),
    reorderInspectionTemplateChecklistItem: (templateId, checklistItemId, reorderDto, projectId) =>
      inspectionTemplateClient.reorderChecklistItem({ templateId, checklistItemId, reorderDto, projectId }),

    createChecklistSection: checklistSectionClient.create,
    updateChecklistSection: checklistSectionClient.edit,
    removeChecklistSection: checklistSectionClient.remove,
    reorderChecklistSection: checklistSectionClient.reorder,
    deleteChecklistSectionDocument: checklistSectionClient.removeDocument,

    createChecklistAnswer: checklistAnswerClient.create,
    updateChecklistAnswer: checklistAnswerClient.edit,
    removeChecklistAnswer: checklistAnswerClient.remove,

    resetInspectionTemplate: () => {
      latestRead += 1;
      set(getDefaultState());
    },
  };
};

export type { InspectionTemplateSlice } from './types';
